import React, { useRef, useState, useEffect } from "react";
import styles from "./CropControl.module.css";

interface CropControlsProps {
  imageSrc: string;
  handleCropData?: (offset: { x: number; y: number }, scale: number) => void;
}

const CropControls: React.FC<CropControlsProps> = ({
  imageSrc,
  handleCropData = null,
}) => {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDrag, setStartDrag] = useState<{ x: number; y: number } | null>(
    null
  );

  const imageRef = useRef<HTMLImageElement>(null);

  const handleParentCropData = (
    newOffset?: { x: number; y: number },
    newZoom?: number
  ) => {
    if (!handleCropData) return;
    console.log("newZom", newZoom);
    if (newOffset && newZoom) {
      handleCropData(newOffset, newZoom);
    } else if (newOffset) {
      handleCropData(newOffset, zoom);
    } else if (newZoom) {
      handleCropData(offset, newZoom);
    }
  };

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    handleParentCropData({ x: 0, y: 0 }, undefined);
    setIsDragging(false);
    setStartDrag(null);

    // only rerun on image change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc]);

  const handleImageLoad = () => {
    const container = imageRef.current?.parentElement;
    const containerWidth = container ? container.clientWidth : 0;
    const containerHeight = container ? container.clientHeight : 0;
    const imageWidth = imageRef.current?.clientWidth || 0;
    const imageHeight = imageRef.current?.clientHeight || 0;

    const maxUnZoomX = containerWidth / imageWidth;
    const maxUnZoomY = containerHeight / imageHeight;
    const maxAllowedUnzoom = Math.max(maxUnZoomX, maxUnZoomY);

    //find a solution here gradient to border
    setZoom(maxAllowedUnzoom + 0.05);
    handleParentCropData(undefined, maxAllowedUnzoom);
  };

  const isTouchEvent = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ): e is React.TouchEvent<HTMLDivElement> => {
    return "touches" in e;
  };

  const handleMouseDown = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    setIsDragging(true);

    const startX = isTouchEvent(e) ? e.touches[0].clientX : e.clientX;
    const startY = isTouchEvent(e) ? e.touches[0].clientY : e.clientY;

    setStartDrag({ x: startX, y: startY });
  };

  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (isDragging && startDrag) {
      const currentX = isTouchEvent(e) ? e.touches[0].clientX : e.clientX;
      const currentY = isTouchEvent(e) ? e.touches[0].clientY : e.clientY;

      const deltaX = currentX - startDrag.x;
      const deltaY = currentY - startDrag.y;

      const newOffsetX = offset.x + deltaX;
      const newOffsetY = offset.y + deltaY;

      const container = imageRef.current?.parentElement;
      const containerWidth = container ? container.clientWidth : 0;
      const containerHeight = container ? container.clientHeight : 0;
      const imageWidth = imageRef.current?.clientWidth || 0;
      const imageHeight = imageRef.current?.clientHeight || 0;

      const minX = containerWidth - imageWidth * zoom;
      const maxX = 0;
      const minY = containerHeight - imageHeight * zoom;
      const maxY = 0;

      const newOffset = {
        x: Math.min(Math.max(newOffsetX, minX), maxX),
        y: Math.min(Math.max(newOffsetY, minY), maxY),
      };
      setOffset(newOffset);
      handleParentCropData(newOffset, undefined);

      setStartDrag({ x: currentX, y: currentY });
      console.log("newOffset", newOffset);
    }
  };

  const handleMouseUp = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? -0.025 : 0.025;

    if (zoomDelta > 0) {
      handleParentCropData(undefined, Math.min(zoom + zoomDelta, 3));
      setZoom((prev) => Math.min(prev + zoomDelta, 3));
    } else {
      const container = imageRef.current?.parentElement;
      const containerWidth = container ? container.clientWidth : 0;
      const containerHeight = container ? container.clientHeight : 0;
      const imageWidth = imageRef.current?.clientWidth || 0;
      const imageHeight = imageRef.current?.clientHeight || 0;

      const minZoomX = containerWidth / (imageWidth * zoom + offset.x);
      const minZoomY = containerHeight / (imageHeight * zoom + offset.y);
      const minZoom = Math.max(minZoomX, minZoomY);

      if (zoom + zoomDelta > minZoom * zoom) {
        handleParentCropData(undefined, zoom + zoomDelta);
        setZoom((prev) => prev + zoomDelta);
      }
    }

    console.log("zoom", zoom);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={handleWheel}
      onTouchStart={handleMouseDown} // Reuse mouse down handler for touch
      onTouchMove={handleMouseMove} // Reuse mouse move handler for touch
      onTouchEnd={handleMouseUp} // Reuse mouse up handler for touch
      className={styles.container}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      {imageSrc && (
        <img
          ref={imageRef}
          src={imageSrc}
          alt="To crop"
          className={styles.image}
          onLoad={handleImageLoad}
          style={{
            top: `${offset.y}px`,
            left: `${offset.x}px`,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
          }}
        />
      )}
    </div>
  );
};

export default CropControls;
